import { Fragment, useEffect, useMemo } from "react";

import clsx from "clsx";

import { useUser } from "@components/user-context";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import {
  COMPARE_METRIC_1_BAR_CHART_PATTERN,
  COMPARE_METRIC_1_LINE_DASH,
  COMPARE_METRIC_2_BAR_CHART_PATTERN,
  COMPARE_METRIC_2_LINE_DASH,
} from "@features/sales-v3/utils/chart";
import { buildCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { FloatingDelayGroup } from "@floating-ui/react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { renderChevronAnimation } from "@shared/selects/utils";

import { MetricFilters } from "../metric-filters";

import { AccountingModeSelector } from "./accounting-mode-selector";
import { AttributionModelSelector } from "./attribution-model-selector";
import { AttributionWindowSelector } from "./attribution-window-selector";
import { MetricSelector } from "./metric-selector";
import { TimePeriodSelector } from "./time-period-selector";

interface CompareMenuProps {
  metrics: SalesPageMetric[];
  compare: Compare;
  updateCompare: (compare: Compare) => void;
  deleteCompare: () => void;
  isSecondComparison?: boolean;
  isBarChart: boolean;
  compareIndex?: number;
  disabled?: boolean;
}

export const CompareMenu = ({
  metrics,
  updateCompare,
  deleteCompare,
  compare,
  isBarChart,
  compareIndex,
  isSecondComparison = false,
  disabled = false,
}: CompareMenuProps) => {
  const { user } = useUser();
  const attributionMethodEnum = user.attributionMethodEnum;
  const { compareDateRange } = usePageConfiguration();
  const getCompareQueryMetadata = buildCompareQueryMetadata({
    metrics,
    attributionMethodEnum,
    compareDateRange,
  });
  const compareQueryMetadata = useMemo(
    () => getCompareQueryMetadata(compare),
    [compare, getCompareQueryMetadata],
  );

  // Fixes the edge case where switching between accounting modes would change available metrics
  useEffect(() => {
    if (
      compareQueryMetadata.optionLabel === "" &&
      compare.queryKey === "metricName" &&
      compare.queryValue !== ""
    ) {
      deleteCompare();
    }
  }, [compareQueryMetadata, deleteCompare, compare]);

  const updateMetricFilters = (
    metricFilters: Compare["filters"],
    ignoreTableFilters: Compare["ignoreTableFilters"],
  ) =>
    updateCompare({ ...compare, filters: metricFilters, ignoreTableFilters });

  const legend = buildLegend(
    {
      borderColor: "#7081FF",
      backgroundColor:
        compareIndex === 0
          ? COMPARE_METRIC_1_BAR_CHART_PATTERN
          : COMPARE_METRIC_2_BAR_CHART_PATTERN,
      borderDash:
        compareIndex === 0
          ? COMPARE_METRIC_1_LINE_DASH
          : COMPARE_METRIC_2_LINE_DASH,
    },
    isBarChart,
  );

  const hasCompare = compareQueryMetadata.label !== "";

  return (
    <>
      <Popover
        as="span"
        className={clsx("relative", { "pointer-events-none": disabled })}
      >
        {({ open }) => (
          <>
            <PopoverButton
              as="div"
              disabled={disabled}
              className={clsx("nb-input-container w-44", {
                "opacity-60": disabled,
              })}
            >
              <div className="flex items-center space-between w-full mb-1">
                <div className="text-xs flex-grow">COMPARE TO</div>
                {hasCompare && (
                  <div dangerouslySetInnerHTML={{ __html: legend }} />
                )}
              </div>
              <div
                className={clsx(
                  "nb-input flex justify-between items-center bg-white cursor-pointer",
                  { "border border-cosmic ring-1 ring-cosmic": open },
                )}
              >
                <div
                  className={clsx(
                    "text-ellipsis whitespace-nowrap overflow-hidden",
                    "max-w-full text-sm flex-grow text-left",
                  )}
                >
                  {compareQueryMetadata.icon && (
                    <span className="mr-2 text-cosmic">
                      {compareQueryMetadata.icon}
                    </span>
                  )}
                  {(compareQueryMetadata.optionLabel as string) || (
                    <span className="text-disabled">Select...</span>
                  )}
                </div>
                <span className="pl-2 justify-self-end" onClick={deleteCompare}>
                  {compareQueryMetadata.optionLabel ? (
                    <i
                      className={clsx(
                        "fa-regular fa-sharp fa-circle-xmark text-sm",
                        "group-hover:text-cosmic+1 text-cosmic",
                      )}
                    />
                  ) : (
                    renderChevronAnimation(open)
                  )}
                </span>
              </div>
            </PopoverButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <PopoverPanel
                className={clsx(
                  "absolute rounded-md bg-white elevation-1",
                  "pt-3 pb-2 left-0 w-56 z-10 mt-2",
                )}
              >
                <FloatingDelayGroup delay={{ open: 0, close: 2000 }}>
                  <MetricSelector
                    metrics={metrics}
                    compare={compare}
                    updateCompare={updateCompare}
                    getCompareQueryMetadata={getCompareQueryMetadata}
                  />
                  <TimePeriodSelector
                    compare={compare}
                    updateCompare={updateCompare}
                    getCompareQueryMetadata={getCompareQueryMetadata}
                    isDisabled={isSecondComparison}
                  />
                  <AttributionModelSelector
                    compare={compare}
                    updateCompare={updateCompare}
                    getCompareQueryMetadata={getCompareQueryMetadata}
                    isDisabled={isSecondComparison}
                  />
                  <AttributionWindowSelector
                    compare={compare}
                    updateCompare={updateCompare}
                    getCompareQueryMetadata={getCompareQueryMetadata}
                    isDisabled={isSecondComparison}
                  />
                  <AccountingModeSelector
                    compare={compare}
                    updateCompare={updateCompare}
                    getCompareQueryMetadata={getCompareQueryMetadata}
                    isDisabled={isSecondComparison}
                  />
                </FloatingDelayGroup>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
      <MetricFilters
        isAddFilterDisabled={compareQueryMetadata.label === "" || disabled}
        metricFilters={compare.filters}
        ignoreTableFilters={compare.ignoreTableFilters}
        updateMetricFilters={updateMetricFilters}
      />
    </>
  );
};
